/** Minimal storage contract keeps device identity logic testable outside the browser. */
export interface DeviceIdentityStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const DEVICE_KEY_VERSION = "sonox.im.device-id.v2";

/**
 * Returns a stable device ID scoped to one open-platform application and SSO user.
 *
 * A browser can sign in as several users over its lifetime. Reusing one global
 * device ID would attempt to bind the same server-side device row to a different
 * user, which is deliberately forbidden. The OIDC subject is used only in the
 * local storage key; the generated opaque device ID is the only value sent to IM.
 */
export function deviceIdForAccessToken(
  accessToken: string,
  clientId: string,
  storage: DeviceIdentityStorage,
): string {
  const subject = accessTokenSubject(accessToken);
  const key = [DEVICE_KEY_VERSION, clientId, subject].map(encodeURIComponent).join(":");
  const existing = storage.getItem(key);
  if (existing !== null && existing.length >= 1 && existing.length <= 128) return existing;

  const created = `web_${crypto.randomUUID()}`;
  storage.setItem(key, created);
  return created;
}

/** Reads only the public JWT subject; signature verification remains server-side. */
function accessTokenSubject(accessToken: string): string {
  const payload = accessToken.split(".")[1];
  if (payload === undefined) throw new Error("登录凭据格式无效，请重新登录");
  try {
    const normalized = payload.replaceAll("-", "+").replaceAll("_", "/");
    const json = atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "="));
    const parsed = JSON.parse(json) as { readonly sub?: unknown };
    if (typeof parsed.sub !== "string" || parsed.sub.length < 1 || parsed.sub.length > 128) {
      throw new TypeError("invalid subject");
    }
    return parsed.sub;
  } catch {
    throw new Error("登录凭据格式无效，请重新登录");
  }
}
